/* HeroCarouselManager — Media ▸ Website ▸ Home page carousel (AJ 2026-10-14).
 * Shared by AD, Scheduler and Tech Specialist. OL owns the pictures (up to 10): the
 * website shows the ticked ones, in this order, with this text. Rows = max(slide
 * count, pictures uploaded); empty rows are upload boxes. No slugs. Doc: MEDIA_PAGE.md */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ArrowDown, ArrowUp, ImagePlus, Loader2, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const src = (u) => (!u ? '' : u.startsWith('/api/') ? `${API || ''}${u}` : u);
const TEXT = [['eyebrow', 'Small label', 'e.g. 2026 STATE CHAMPIONS'], ['title', 'Headline', 'Big title'],
  ['subtitle', 'Sub-text', ''], ['cta_text', 'Button 1', 'e.g. Register'], ['cta_url', 'Button 1 link', 'https://…'],
  ['cta2_text', 'Button 2', ''], ['cta2_url', 'Button 2 link', 'https://…']];

export default function HeroCarouselManager({ orgId, isDark }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const base = `${API}/api/website-media/${orgId}/hero`;
  const err = (e, fb) => window.alert(e?.response?.data?.detail || fb);

  const load = useCallback(async () => {
    try { const { data: d } = await axios.get(base); setData(d.data); }
    catch (e) { setData({ settings: {}, slides: [], max: 10 }); err(e, 'Could not load the carousel'); }
  }, [base]);
  useEffect(() => { load(); }, [load]);

  const setSetting = async (k, v) => {
    try { const { data: d } = await axios.put(`${base}/settings`, { [k]: v }); setData(d.data); }
    catch (e) { err(e, 'Save failed'); }
  };
  // Optimistic: the row (Show tick, text) changes the moment the user acts; the save and the
  // website update run behind it. Only a failed save puts the row back (AJ 2026-10-24).
  const patch = async (s, body) => {
    setData((p) => (p ? { ...p, slides: (p.slides || []).map((x) => (x.id === s.id ? { ...x, ...body } : x)) } : p));
    try { await axios.patch(`${base}/slides/${s.id}`, body); } catch (e) { err(e, 'Save failed'); load(); }
  };
  const upload = async (file, slide) => {
    const fd = new FormData(); fd.append('file', file);
    setBusy(slide?.id || 'new');
    try {
      await axios.post(slide ? `${base}/slides/${slide.id}/image` : `${base}/slides`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      await load();
    } catch (e) { err(e, 'Upload failed'); } finally { setBusy(null); }
  };
  const del = async (s) => {
    if (!window.confirm('Delete this picture? It is removed from the website too.')) return;
    try { await axios.delete(`${base}/slides/${s.id}`); await load(); } catch (e) { err(e, 'Delete failed'); }
  };
  const move = async (i, dir) => {
    const ids = data.slides.map((s) => s.id); const j = i + dir;
    if (j < 0 || j >= ids.length || busy) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    setBusy('order');
    try { const { data: d } = await axios.put(`${base}/order`, { ids }); setData((p) => ({ ...p, slides: d.data })); }
    catch (e) { err(e, 'Reorder failed'); } finally { setBusy(null); }
  };

  if (!data) return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-amber-500" /></div>;
  const { settings, slides, max } = data;
  const count = Number(settings.slide_count || 3);
  const shown = slides.filter((s) => s.show).length;
  const empties = Math.max(0, Math.min(max, count) - slides.length);
  const muted = isDark ? 'text-slate-400' : 'text-slate-500';
  const cell = `w-full bg-transparent rounded px-1 py-0.5 text-xs border border-transparent hover:border-slate-300 focus:border-amber-500 focus:outline-none ${isDark ? 'text-white hover:border-slate-600' : 'text-slate-900'}`;
  const th = `px-1.5 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wide ${muted}`;
  const sel = `h-7 text-xs rounded px-1 border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`;

  return (
    <div className="space-y-2" data-testid="hero-manager">
      <div className="flex items-center gap-3 flex-wrap text-xs">
        <label className={muted}>Slides shown
          <select value={count} onChange={(e) => setSetting('slide_count', Number(e.target.value))} className={`${sel} ml-1`} data-testid="hero-count">
            {Array.from({ length: max }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <label className={muted}>Seconds per slide
          <select value={settings.autoplay_seconds || 6} onChange={(e) => setSetting('autoplay_seconds', Number(e.target.value))} className={`${sel} ml-1`}>
            {[3, 4, 5, 6, 8, 10, 12, 15, 20, 30].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <label className={muted}>Darken picture
          <select value={settings.overlay_dim ?? 70} onChange={(e) => setSetting('overlay_dim', Number(e.target.value))} className={`${sel} ml-1`}>
            {[...new Set([0, 20, 40, 50, 60, 70, 80, 90, Number(settings.overlay_dim ?? 70)])].sort((a, b) => a - b)
              .map((n) => <option key={n} value={n}>{n}%</option>)}
          </select>
        </label>
        <label className={`inline-flex items-center gap-1 ${muted}`}>
          <input type="checkbox" checked={settings.show_overlay_text !== false} onChange={(e) => setSetting('show_overlay_text', e.target.checked)} />
          Show text on pictures
        </label>
        <span className={`ml-auto ${muted}`}>{shown} of {count} shown · {slides.length}/{max} pictures</span>
      </div>
      <div className={`overflow-x-auto rounded border ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
        <table className="w-full text-xs" data-testid="hero-table">
          <thead className={isDark ? 'bg-slate-900/60' : 'bg-slate-50'}>
            <tr><th className={th}>Show</th><th className={th}>Picture</th>{TEXT.map(([k, l]) => <th key={k} className={th}>{l}</th>)}<th className={th} /></tr>
          </thead>
          <tbody>
            {slides.map((s, i) => (
              <tr key={s.id} className={`border-t ${isDark ? 'border-slate-700/60' : 'border-slate-100'} ${s.show ? '' : 'opacity-60'}`} data-testid={`hero-row-${i}`}>
                <td className="px-1.5 py-1 text-center">
                  <input type="checkbox" checked={!!s.show} disabled={!s.show && shown >= count}
                    title={!s.show && shown >= count ? `Only ${count} show — untick one or raise "Slides shown"` : 'Show on the website'}
                    onChange={(e) => patch(s, { show: e.target.checked })} data-testid={`hero-show-${i}`} />
                </td>
                <td className="px-1 py-1"><Pic url={src(s.ol_url || s.wp_url)} busy={busy === s.id} onFile={(f) => upload(f, s)} isDark={isDark} testid={`hero-pic-${i}`} /></td>
                {TEXT.map(([k, , ph]) => (
                  <td key={k} className={`px-1 py-1 ${k.endsWith('url') ? 'min-w-[120px]' : k === 'subtitle' || k === 'title' ? 'min-w-[160px]' : 'min-w-[100px]'}`}>
                    <input defaultValue={s[k] || ''} key={`${s.id}-${k}-${s[k]}`} placeholder={ph} className={cell}
                      onBlur={(e) => { if (e.target.value !== (s[k] || '')) patch(s, { [k]: e.target.value }); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }} data-testid={`hero-${k}-${i}`} />
                  </td>
                ))}
                <td className="px-1 py-1 whitespace-nowrap">
                  <button type="button" title="Move up" onClick={() => move(i, -1)} disabled={i === 0} className="p-0.5 text-slate-400 hover:text-amber-500 disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
                  <button type="button" title="Move down" onClick={() => move(i, 1)} disabled={i === slides.length - 1} className="p-0.5 text-slate-400 hover:text-amber-500 disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
                  <button type="button" title="Delete picture" onClick={() => del(s)} className="p-0.5 text-red-400 hover:text-red-600" data-testid={`hero-del-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </td>
              </tr>
            ))}
            {Array.from({ length: empties || (slides.length < max ? 1 : 0) }, (_, k) => (
              <tr key={`new-${k}`} className={`border-t ${isDark ? 'border-slate-700/60' : 'border-slate-100'}`}>
                <td className={`px-1.5 py-1 text-center ${muted}`}>—</td>
                <td className="px-1 py-1"><Pic url="" busy={busy === 'new' && k === 0} onFile={(f) => upload(f, null)} isDark={isDark} testid={`hero-new-${k}`} /></td>
                <td colSpan={TEXT.length + 1} className={`px-2 py-1 text-[11px] ${muted}`}>Add a picture{slides.length + k < count ? ` (slide ${slides.length + k + 1} of ${count})` : ''} — then fill in its text.</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className={`text-[10px] ${muted}`}>The website shows the ticked pictures, top to bottom. Pictures are stored by OL and copied to the website automatically.</p>
    </div>
  );
}

function Pic({ url, busy, onFile, isDark, testid }) {
  const ref = useRef(null);
  return (
    <div className="relative group h-12 w-20" data-testid={testid}>
      {url ? <img src={url} alt="" className="h-12 w-20 object-cover rounded border border-slate-200" />
        : <div className={`h-12 w-20 rounded border border-dashed flex items-center justify-center ${isDark ? 'border-slate-600' : 'border-slate-300'}`}><ImagePlus className="h-4 w-4 text-slate-400" /></div>}
      {busy ? <div className="absolute inset-0 bg-black/40 rounded flex items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-white" /></div>
        : <button type="button" onClick={() => ref.current?.click()} title={url ? 'Replace picture' : 'Upload picture'}
          className="absolute inset-0 rounded bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white"><ImagePlus className="h-4 w-4" /></button>}
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onFile(f); }} />
    </div>
  );
}
